import { FC, useMemo, useRef } from 'react'

import styled from 'styled-components'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'

import { variables } from 'src/Styled/variables'

import { ChartData } from './useChart'
import { usePerformanceChart } from './usePerformanceChart'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
)

const ChartContainer = styled.div`
  min-width: 0;
  overflow: hidden;

  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  border-radius: 16px;
  padding-bottom: 24px;
`

const ChartTitle = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-size: ${variables.fontSize20};
  font-style: normal;
  font-weight: ${variables.fontWeight600};
  line-height: 150%;
  align-self: flex-start;
  padding-left: 24px;
  padding-top: 24px;
`

const LoadingText = styled.div`
  text-align: center;
  padding: 40px;
  color: ${({ theme }) => theme.color.slateGrey};
`
const ChartInnerContainer = styled.div`
  position: relative;
  width: 100%;
  height: 350px;
  cursor: pointer;
  padding: 0 16px;

  canvas {
    display: block;
    width: 100% !important;
    height: 100% !important;
  }
`

interface PerformanceChartProps {
  chartData?: ChartData | null
  strategy?: string
}

export const PerformanceChart: FC<PerformanceChartProps> = ({
  chartData,
  strategy,
}) => {
  const { options, legendSpacingPlugin } = usePerformanceChart(strategy)

  const chartRef = useRef<ChartJS<'line'> | null>(null)

  const fixedOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      ...options,
    }),
    [options],
  )

  if (!chartData) {
    return (
      <ChartContainer>
        <ChartTitle>Performance chart</ChartTitle>
        <LoadingText>No data available for the selected period</LoadingText>
      </ChartContainer>
    )
  }

  return (
    <ChartContainer>
      <ChartInnerContainer>
        <Line
          ref={chartRef}
          data={chartData}
          options={fixedOptions}
          plugins={[legendSpacingPlugin]}
        />
      </ChartInnerContainer>
    </ChartContainer>
  )
}
